import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import authBg from "../../assets/bg.avif";

type LoginForm = {
  email: string;
  password: string;
  remember: boolean;
};

const ADMIN_AUTH_KEY = "mediit_admin_auth";

const AdminAuth: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [loginForm, setLoginForm] = useState<LoginForm>({
    email: "",
    password: "",
    remember: true,
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!loginForm.email.trim() || !loginForm.password.trim()) {
      setError("Email aur password required hai.");
      return;
    }

    if (!loginForm.email.includes("@")) {
      setError("Please valid email enter karo.");
      return;
    }

    setIsLoading(true);
    try {
      // Local testing ke liye localhost, production ke liye live url
      // const response = await fetch("http://localhost:9091/api/admin/auth/login", {
      const response = await fetch("https://api.mediitacademy.com/api/admin/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginForm.email, password: loginForm.password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      localStorage.setItem(
        ADMIN_AUTH_KEY,
        JSON.stringify({
          isAuthenticated: true,
          email: loginForm.email,
          name: data.name,
          role: data.role,
          token: data.token,
          loginAt: new Date().toISOString(),
        })
      );

      const role = String(data.role || "").toLowerCase();

      // Role based routing
      if (role === "teacher") {
        navigate("/teach-with-us/dashboard", { replace: true });
      } else {
        navigate("/learn-with-us", { replace: true });
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-slate-950 bg-cover bg-center bg-no-repeat text-slate-950 font-['Inter']"
      style={{
        backgroundImage: `url(${authBg})`,
      }}
    >
      <main className="relative mx-auto grid min-h-screen max-w-[1500px] items-center gap-8 px-4 py-6 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
        {/* LEFT SECTION - INFO */}
        <section className="hidden lg:block">
          <div className="relative overflow-hidden rounded-[38px] border border-slate-200 bg-white p-8 shadow-2xl shadow-slate-300/40 xl:p-10">
            <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-blue-200/60 blur-3xl" />
            <div className="absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-cyan-200/60 blur-3xl" />

            <div className="relative">
              <div className="mb-8 flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-lg shadow-blue-500/20">
                  <img
                    src={logo}
                    alt="Mediit Academy Logo"
                    className="h-full w-full object-contain p-1"
                  />
                </div>
                <div>
                  <h1 className="text-2xl font-black text-slate-950">Mediit Academy</h1>
                  <p className="text-sm font-bold text-slate-500">Secure LMS Admin Portal</p>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-xs font-black uppercase tracking-wide text-blue-700">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                Admin Security Gateway
              </div>

              <h2 className="mt-6 max-w-2xl text-4xl font-black leading-tight tracking-tight text-slate-950 xl:text-5xl">
                Secure login before opening your LMS dashboard.
              </h2>

              <p className="mt-5 max-w-xl text-base font-semibold leading-7 text-slate-600">
                Admin panel ke andar jane se pehle login, role check aur session protection ready hai.
              </p>

              <div className="mt-8 overflow-hidden rounded-[34px] border border-slate-200 bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-6 shadow-sm">
                <div className="grid items-center gap-6 xl:grid-cols-[0.95fr_1.05fr]">
                  <div>
                    <p className="text-xs font-black uppercase tracking-wide text-blue-600">
                      Secure Access Flow
                    </p>
                    <h3 className="mt-3 text-2xl font-black leading-tight text-slate-950">
                      Only verified admin can enter dashboard.
                    </h3>
                    <p className="mt-3 text-sm font-semibold leading-6 text-slate-500">
                      Login hone ke baad hi /admin routes open honge. Logout karte hi session clear ho jayega.
                    </p>

                    <div className="mt-5 space-y-3">
                      {[
                        "Email & password validation",
                        "Protected admin routing",
                        "Role based access ready",
                      ].map((item) => (
                        <div key={item} className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-sm">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-xs font-black text-emerald-700">
                            ✓
                          </span>
                          <p className="text-xs font-black text-slate-600">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Vactor / Illustration */}
                  <div className="relative mx-auto flex h-72 w-full max-w-[360px] items-center justify-center">
                    <div className="absolute h-64 w-64 rounded-full bg-blue-200/60 blur-2xl" />
                    <div className="relative h-full w-full">
                      <svg viewBox="0 0 420 330" className="h-full w-full drop-shadow-xl">
                        <rect x="72" y="42" width="276" height="200" rx="28" fill="#0f172a" />
                        <rect x="96" y="70" width="228" height="24" rx="12" fill="#1e293b" />
                        <rect x="96" y="112" width="150" height="16" rx="8" fill="#334155" />
                        <rect x="96" y="142" width="190" height="16" rx="8" fill="#334155" />
                        <rect x="96" y="178" width="86" height="44" rx="14" fill="#2563eb" />
                        <rect x="196" y="178" width="86" height="44" rx="14" fill="#06b6d4" />
                        <circle cx="298" cy="126" r="34" fill="#22c55e" />
                        <path d="M282 126l11 11 24-27" fill="none" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M210 118c42 0 78 30 86 70-16 48-48 76-86 92-38-16-70-44-86-92 8-40 44-70 86-70Z" fill="#ffffff" />
                        <path d="M210 138c30 0 55 21 61 50-12 34-35 55-61 68-26-13-49-34-61-68 6-29 31-50 61-50Z" fill="#dbeafe" />
                        <circle cx="210" cy="188" r="25" fill="#2563eb" />
                        <rect x="198" y="184" width="24" height="36" rx="6" fill="#0f172a" />
                        <path d="M194 184v-12c0-10 7-18 16-18s16 8 16 18v12" fill="none" stroke="#0f172a" strokeWidth="8" strokeLinecap="round" />
                        <circle cx="118" cy="262" r="18" fill="#38bdf8" />
                        <circle cx="304" cy="268" r="22" fill="#60a5fa" />
                        <circle cx="76" cy="116" r="12" fill="#22c55e" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* RIGHT SECTION - LOGIN FORM */}
        <section className="mx-auto w-full max-w-[620px]">
          <div className="mb-5 flex items-center justify-center gap-3 lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-lg shadow-blue-500/20">
              <img src={logo} alt="Mediit Academy Logo" className="h-full w-full object-contain p-1" />
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-950">Mediit Academy</h1>
              <p className="text-xs font-bold text-slate-500">Secure Admin Portal</p>
            </div>
          </div>

          <div className="rounded-[34px] border border-white bg-white/90 p-5 shadow-2xl shadow-slate-300/40 backdrop-blur-xl sm:p-7">
            <div className="mb-6">
              <p className="text-xs font-black uppercase tracking-wide text-blue-600">
                Welcome Back
              </p>
              <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                Login to Admin Panel
              </h2>
              <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
                Admin dashboard access ke liye secure login required hai.
              </p>
            </div>

            {error && (
              <div className="mb-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-black text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <InputBox
                label="Email Address"
                type="email"
                placeholder="admin@mediitacademy.com"
                value={loginForm.email}
                onChange={(value) => setLoginForm({ ...loginForm, email: value })}
              />

              <InputBox
                label="Password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter admin password"
                value={loginForm.password}
                onChange={(value) => setLoginForm({ ...loginForm, password: value })}
                rightText={showPassword ? "Hide" : "Show"}
                onRightClick={() => setShowPassword(!showPassword)}
              />

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-bold text-slate-600">
                  <input
                    type="checkbox"
                    checked={loginForm.remember}
                    onChange={(e) => setLoginForm({ ...loginForm, remember: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Remember this device
                </label>

                <button type="button" className="text-left text-sm font-black text-blue-600 hover:text-blue-700">
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full rounded-2xl px-5 py-4 text-sm font-black text-white shadow-lg transition ${
                  isLoading
                    ? "bg-blue-400 cursor-not-allowed shadow-none"
                    : "bg-blue-600 shadow-blue-600/25 hover:bg-blue-700"
                }`}
              >
                {isLoading ? "Authenticating..." : "Login Securely"}
              </button>

              <div className="rounded-2xl bg-slate-50 p-4 text-sm font-semibold text-slate-500">
                Welcome back! Enter your registered email and password to securely access your Mediit Academy dashboard.
              </div>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
};

const InputBox = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  rightText,
  onRightClick,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  type?: string;
  rightText?: string;
  onRightClick?: () => void;
}) => {
  return (
    <div>
      <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </label>
      <div className="flex items-center rounded-2xl border border-slate-200 bg-slate-50 px-4 focus-within:border-blue-500 focus-within:bg-white">
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="h-13 min-h-[52px] w-full bg-transparent text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400"
        />
        {rightText && (
          <button
            type="button"
            onClick={onRightClick}
            className="ml-3 text-xs font-black text-blue-600 hover:text-blue-700"
          >
            {rightText}
          </button>
        )}
      </div>
    </div>
  );
};

export const getAdminAuth = () => {
  const auth = localStorage.getItem(ADMIN_AUTH_KEY);
  if (!auth) return null;

  try {
    return JSON.parse(auth);
  } catch {
    return null;
  }
};

export const clearAdminAuth = () => {
  localStorage.removeItem(ADMIN_AUTH_KEY);
};

export default AdminAuth;